import { styled } from "@mui/material";
import { useState, useId, useRef } from "react";
import { ModalTitle, ModalActions } from "@kaltura/ds-react-components";
import { CreationToolWrapperActions } from "./CreationToolWrapperActions";
import {
    CreationToolWrapperProps,
    ConfirmationModal,
    CreationToolProps,
} from "@kaltura/mediaspace-shared-search-and-select";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { CustomComponent, ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { SearchAndSelectEntry } from "@kaltura/mediaspace-shared-types";
import { KalturaBaseEntry } from "kaltura-typescript-client/api/types/KalturaBaseEntry";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";

export interface AddToCategoryCreationToolWrapperProps extends CreationToolWrapperProps {
    touLink?: string;
    onCancel: () => void;
    onSubmit: (entryIds: string[]) => void;
}

const StyledActionsBar = styled("div")(({ theme }) => ({
    width: "100%",
}));

/**
 * creation tool (multi-upload, express recorder .etc) wrapper for Add Content.
 *
 * creation tools should contain <ModalTitle>, <ModalContent /> and optionally <ModalActions>
 *
 * entry creation flow is as follows:
 * 1. an entry gets created (no content yet)
 * 2. a created entry gets its content uploaded
 * 3. an uploaded entry gets its metadata edited, in the process becoming ready/unready for publish.
 *
 * when all the entries are created, uploaded and are ready for publish, we can `save and select` them.
 * save and select can happen in one of two ways:
 * 1. and edit entry form is shown, and may have some unsaved changes in it. as long as the entry is ready for
 *     publish - we can proceed. we will submit this form, and when its done we will select all the entries.
 * 2. no edit entry form is present - we will just select all the entries.
 *
 * entries can be saved before upload is done, but then we will only save them, without the select bit.
 */
export const CreationToolWrapper = (props: AddToCategoryCreationToolWrapperProps) => {
    const { creationTool, preCreationToolComponent, onBack, onCancel, onSubmit, touLink } = props;
    const [entries, setEntries] = useState<KalturaBaseEntry[]>([]);
    const [showPreCreationTool, setShowPreCreationTool] = useState(!!preCreationToolComponent);
    const [finishedUploading, setFinishedUploading] = useState(false);
    const [readyForPublish, setReadyForPublish] = useState(false);
    const [editFormShown, setEditFormShown] = useState(false);
    const [canSaveForm, setCanSaveForm] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [confirmationModalAction, setConfirmationModalAction] = useState<(() => void) | undefined>();
    const saveAndSelectClicked = useRef(false);
    const uploadDoneRef = useRef(false);

    const formId = useId();

    const componentProps = creationTool.props as CreationToolProps;

    const hasEntries = entries.length > 0;

    const handleEntryCreated = ({ entry }: SearchAndSelectEntry) => {
        setEntries((prevEntries) => [...prevEntries, entry]);
    };

    const handleUploadCancelled = (entryIds?: string[]) => {
        setEntries((prevEntries) => prevEntries.filter((entry) => !entryIds?.includes(entry.id)));
    };

    const handleOnEditFormShown = (showingForm: boolean) => {
        setEditFormShown(showingForm);
    };

    const handleActionsSubmit = () => {
        // set ref - setting state will prevent the form submit
        saveAndSelectClicked.current = true;

        // save the finishedUploading state in ref, for use in handle submit
        // in case uploading was not finished yet, but may be until save is done
        uploadDoneRef.current = finishedUploading;

        // no edit form - no form submit
        !editFormShown && setProcessing(true);
        // no edit form + finished uploading - do own submit
        !editFormShown && finishedUploading && onSubmit(entries.map((entry) => entry.id));
    };

    const handleSubmitStarted = () => {
        // was this submit triggered by `save and select` (handleActionsSubmit)
        saveAndSelectClicked.current && editFormShown && setProcessing(true);
    };

    const handleSubmit = () => {
        // Proceed to selecting the entry only if the upload has finished as we
        // clicked `save and select` in the actions section
        if (processing && uploadDoneRef.current) {
            onSubmit(entries.map((entry) => entry.id));
        }
        else {
            setProcessing(false);
        }
    };

    const handleCancel = () => {
        if (entries.length > 0 && (!finishedUploading || canSaveForm)) {
            // have uploading entries/unsaved changes - show confirmation modal
            setConfirmationModalAction(() => onCancel);
        }
        else {
            // no entries/changes - cancel away
            onCancel();
        }
    };

    const handleBack = () => {
        if (entries.length > 0 && (!finishedUploading || canSaveForm)) {
            // have uploading entries/unsaved changes - show confirmation modal
            setConfirmationModalAction(() => onBack);
        }
        else {
            // no entries/changes - back away
            onBack();
        }
    };

    const onConfirmationCancel = () => {
        setConfirmationModalAction(undefined);
    };

    return (
        <>
            <ButtonAnalyticsProvider location={`${creationTool.analyticsLocation}`} applicationFeature={"addcontent"}>
                <ModalTitle variant={"h2"}>{componentProps.title}</ModalTitle>

                {showPreCreationTool && preCreationToolComponent && (
                    <CustomComponent
                        component={preCreationToolComponent}
                        onBack={onBack}
                        onSubmit={() => setShowPreCreationTool(false)}
                    />
                )}

                {!showPreCreationTool && (
                    <ErrorBoundary fallback={null}>
                        <CustomComponent
                            component={creationTool}
                            onEntryCreated={handleEntryCreated}
                            onUploadCancelled={handleUploadCancelled}
                            onEntriesFinishedUploading={(finishedUploading: boolean) => {
                                setFinishedUploading(finishedUploading);
                            }}
                            onEntriesReadyPublish={(readyForPublish: boolean) => setReadyForPublish(readyForPublish)}
                            onEditFormShown={handleOnEditFormShown}
                            onCanSaveForm={(canSaveForm: boolean) => setCanSaveForm(canSaveForm)}
                            onSubmitStarted={handleSubmitStarted}
                            onSubmit={handleSubmit}
                            formId={formId}
                            onBack={handleBack}
                            onCancel={handleCancel}
                        />
                        <ConfirmationModal
                            uploadInProgress={!finishedUploading}
                            unSavedChanges={canSaveForm}
                            confirmationModalAction={confirmationModalAction}
                            onCancel={onConfirmationCancel}
                        />
                    </ErrorBoundary>
                )}

                {!showPreCreationTool && (!componentProps.internalActionsBar || readyForPublish || hasEntries) && (
                    <ModalActions>
                        <StyledActionsBar>
                            <CreationToolWrapperActions
                                formId={formId}
                                saveButtonLabel={finishedUploading ? translate("Save & Select") : translate("Save")}
                                onSubmit={handleActionsSubmit}
                                onBack={handleBack}
                                onCancel={handleCancel}
                                touLink={touLink}
                                showActionButtons={readyForPublish || hasEntries}
                                hasEntry={hasEntries}
                                readyForPublish={readyForPublish}
                                finishedUploading={finishedUploading}
                                editFormShown={editFormShown}
                                processing={processing}
                            />
                        </StyledActionsBar>
                    </ModalActions>
                )}
            </ButtonAnalyticsProvider>
        </>
    );
};

export default CreationToolWrapper;
